import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Bookmark, Star, Sparkles, CreditCard,
  Info, FileText, Shield, AlertCircle, LogOut, Trash2, X, Send, Bell,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../context/AuthContext';
import { enablePush, disablePush, pushSupported } from '../lib/push';
import { browserTz, tzLabel } from '../lib/tz';
import './Settings.css';

const APP_VERSION = '2.01';

const settingsItems = [
  { id: 1, icon: Bookmark, label: 'Saved', action: 'saved' },
  { id: 2, icon: Star, label: 'Rating & Review', action: 'rating' },
  { id: 3, icon: Sparkles, label: 'Premium Subscriptions', action: 'subscriptions' },
  { id: 4, icon: CreditCard, label: 'Payment Method', action: 'payment' },
  { id: 5, icon: Info, label: 'About 6th Mate', action: 'about' },
  { id: 6, icon: FileText, label: 'Terms of Use', action: 'terms' },
  { id: 7, icon: Shield, label: 'Privacy Policy', action: 'privacy' },
  { id: 8, icon: AlertCircle, label: 'Report a problem', action: 'report' },
  { id: 9, icon: LogOut, label: 'Logout', action: 'logout' },
  { id: 10, icon: Trash2, label: 'Delete account', action: 'delete', danger: true },
];

const TERMS_TEXT = `Welcome to 6th Mate. By creating an account and using the service you agree to these terms.

1. Eligibility. You must be at least 18 years old to use 6th Mate.

2. Your account. You are responsible for keeping your password safe and for anything that happens under your account. Do not share your login with anyone.

3. Content. You keep ownership of the videos, comments and messages you post. By posting, you grant 6th Mate a non-exclusive licence to display them inside the app. Do not post anything unlawful, hateful, deceptive, sexually explicit, or infringing on someone else's rights.

4. Bookings and payments. When you book a mentor, payment is processed via Stripe. Funds are held until the mentor joins the scheduled call. Cancellations more than 24 hours before the session are fully refundable; cancellations inside 24 hours are not. If a mentor no-shows, you are refunded automatically.

5. Ratings. After every completed session the student may rate the mentor. Ratings must be honest and cannot be used to harass.

6. Behaviour. No harassment, spam, scams, or attempts to move payments off-platform. We may suspend or terminate accounts that break these rules.

7. Termination. You can delete your account at any time from Settings. We may terminate accounts that violate these terms.

8. No warranty. The service is provided "as is" without warranties of any kind.

9. Changes. We may update these terms. Continued use after an update means you accept the new terms.

10. Contact. Questions about these terms: support@6thmate.com.`;

const PRIVACY_TEXT = `6th Mate Privacy Policy

Your privacy matters to us. This policy explains what we collect, how we use it, and how we keep it safe.

What we collect:
• Your account details (name, email, hashed password, profile info you choose to share)
• Content you post (videos, comments, messages)
• Booking and payment records
• Basic device/session info required to run the app

How we use it:
• To operate the app — showing your profile to other mentors and mentees, delivering messages and bookings, processing payments, and sending you notifications you asked for.
• To keep the service safe — detecting abuse, fraud, or violations of our Terms.

Who we share it with:
All accounts and data are handled in-house. Your information will never be shared, sold, or rented to any external party. The only exceptions are our essential service providers (Stripe for payments, Daily.co for video calls, Cloudinary for video hosting, and the Emergent Email Proxy for transactional emails) — each is contractually bound to use your data only to provide their service to 6th Mate.

Your controls:
• Edit or delete profile details from Profile → Settings
• Delete your account any time from Settings → Delete account (this removes your profile, videos, comments and messages)
• Request an export or ask us anything: support@6thmate.com

We keep security tight — passwords are hashed, sensitive payout details are held encrypted, and access to production data is limited to a small support team.

This policy may be updated. We will notify you of material changes inside the app.`;

const REPORT_CATEGORIES = ['Bug / Something broken', 'Payment issue', 'Inappropriate content', 'Another user', 'Suggestion', 'Other'];

const Settings = () => {
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuth();
  const [openDialog, setOpenDialog] = useState(null); // 'about' | 'terms' | 'privacy' | 'report' | 'install' | 'timezone' | null
  const [reportCategory, setReportCategory] = useState(REPORT_CATEGORIES[0]);
  const [reportMessage, setReportMessage] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [savingTz, setSavingTz] = useState(false);
  const [tzSearch, setTzSearch] = useState('');

  const currentTz = user?.timezone || '';
  const detectedTz = browserTz();
  const tzIsAutoDetect = currentTz && currentTz === detectedTz;

  const saveTimezone = async (newTz) => {
    if (!newTz || newTz === currentTz) return;
    setSavingTz(true);
    try {
      const r = await api('/api/users/me', { method: 'PUT', body: JSON.stringify({ timezone: newTz }) });
      if (r.user) {
        setUser(r.user);
        localStorage.setItem('user', JSON.stringify(r.user));
        toast.success('Timezone saved', { description: tzLabel(newTz) });
      }
    } catch (e) {
      toast.error('Could not save timezone', { description: e.message });
    }
    setSavingTz(false);
  };

  // Full IANA zone list for the picker modal. Intl.supportedValuesOf is in
  // every evergreen browser; the hard-coded fallback covers the handful of
  // zones most mentors travel between so the picker never comes up empty.
  const allTimezones = useMemo(() => {
    try {
      if (typeof Intl.supportedValuesOf === 'function') {
        return Intl.supportedValuesOf('timeZone');
      }
    } catch { /* ignore */ }
    return [
      'UTC', 'Australia/Sydney', 'Australia/Melbourne', 'Australia/Perth',
      'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
      'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
      'America/Toronto', 'America/Sao_Paulo', 'Asia/Tokyo', 'Asia/Singapore',
      'Asia/Hong_Kong', 'Asia/Kolkata', 'Asia/Dubai', 'Pacific/Auckland',
    ];
  }, []);

  // Offset for a zone right now, e.g. "+11:00". Used as a sort key AND a
  // helpful tag in the picker so travellers can scan by hour-offset.
  const tzOffset = (tz) => {
    try {
      const dtf = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' });
      const part = dtf.formatToParts(new Date()).find(p => p.type === 'timeZoneName');
      return part ? part.value.replace('GMT', 'UTC') : '';
    } catch { return ''; }
  };

  const filteredTimezones = useMemo(() => {
    const q = tzSearch.trim().toLowerCase();
    const list = q
      ? allTimezones.filter(tz => tz.toLowerCase().includes(q) || tz.toLowerCase().replace(/_/g, ' ').includes(q))
      : allTimezones;
    // Pin current + detected to the top (if present).
    const pinned = [];
    const pinnedSet = new Set();
    [currentTz, detectedTz].forEach(t => {
      if (t && list.includes(t) && !pinnedSet.has(t)) { pinned.push(t); pinnedSet.add(t); }
    });
    const rest = list.filter(t => !pinnedSet.has(t));
    return [...pinned, ...rest];
  }, [tzSearch, allTimezones, currentTz, detectedTz]);

  const pickTimezone = async (tz) => {
    setOpenDialog(null);
    setTzSearch('');
    await saveTimezone(tz);
  };

  useEffect(() => {
    if (!pushSupported()) return;
    (async () => {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      setPushOn(!!sub);
    })();
  }, []);

  const togglePush = async () => {
    if (!pushSupported()) {
      toast.error('Push notifications aren\u2019t supported in this browser. Install 6th Mate from the banner to enable them.');
      return;
    }
    if (pushOn) {
      await disablePush();
      setPushOn(false);
      toast('Push notifications disabled.');
    } else {
      const r = await enablePush();
      if (r.ok) {
        setPushOn(true);
        toast.success('Push notifications enabled — you\u2019ll get alerts for new messages and bookings.');
      } else if (r.reason === 'denied') {
        toast.error('Notifications were blocked. Enable them in your browser settings for 6th Mate.');
      } else {
        toast.error('Could not enable notifications.');
      }
    }
  };

  const handleItemClick = (action) => {
    switch (action) {
      case 'saved':
        return navigate('/settings/saved');
      case 'rating':
        return navigate('/settings/reviews');
      case 'subscriptions':
        return toast.info('Premium Subscriptions coming soon.');
      case 'payment':
        return navigate('/settings/payment');
      case 'about':
      case 'terms':
      case 'privacy':
      case 'report':
        setOpenDialog(action);
        return;
      case 'logout':
        if (window.confirm('Are you sure you want to logout?')) {
          logout?.();
          navigate('/auth');
        }
        return;
      case 'delete':
        if (window.confirm('Delete your account? This cannot be undone.')) {
          toast.error('Delete account is not yet enabled — email support@6thmate.com and we will action it manually.');
        }
        return;
      default:
        return;
    }
  };

  const submitReport = async () => {
    if (reportMessage.trim().length < 5) {
      toast.error('Please describe the problem in at least a few words.');
      return;
    }
    setReportSubmitting(true);
    try {
      await api('/api/support/report', {
        method: 'POST',
        body: JSON.stringify({ category: reportCategory, message: reportMessage.trim() }),
      });
      toast.success('Report sent to support@6thmate.com. Thanks — we\'ll take a look.');
      setReportMessage('');
      setOpenDialog(null);
    } catch (e) {
      toast.error('Could not send report. Please email support@6thmate.com directly.');
    }
    setReportSubmitting(false);
  };

  return (
    <div className="settings-container" data-testid="settings-page">
      <div className="settings-header">
        <button className="settings-back-btn" onClick={() => navigate('/profile')} data-testid="settings-back-btn">
          <ArrowLeft size={24} />
        </button>
        <h1 className="settings-title">Settings</h1>
      </div>

      <div className="settings-list">
        {/* Timezone row — critical for cross-timezone bookings. Tapping
            opens a searchable IANA picker so travelling mentors can switch
            in one tap. */}
        <div
          className="settings-item"
          data-testid="settings-item-timezone"
          onClick={() => setOpenDialog('timezone')}
          style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
        >
          <div className="settings-item-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 500, color: '#111' }}>Timezone</p>
            <p style={{ margin: 0, fontSize: 12, color: '#6B7280' }} data-testid="tz-current">
              {currentTz ? `${tzLabel(currentTz)} · ${currentTz}` : 'Tap to pick your timezone'}
            </p>
          </div>
          <span
            data-testid="tz-change-chip"
            style={{ padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: tzIsAutoDetect ? '#DCFCE7' : '#FEF3C7', color: tzIsAutoDetect ? '#166534' : '#92400E' }}
          >
            {tzIsAutoDetect ? 'AUTO' : 'CHANGE'}
          </span>
        </div>

        {/* Push notifications toggle */}
        <div
          className="settings-item"
          onClick={togglePush}
          data-testid="settings-item-push"
          style={{ borderBottom: '1px solid #f0f0f0' }}
        >
          <div className="settings-item-icon"><Bell size={24} /></div>
          <span className="settings-item-label" style={{ flex: 1 }}>Push notifications</span>
          <span style={{
            padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700,
            background: pushOn ? '#DCFCE7' : '#F3F4F6', color: pushOn ? '#166534' : '#6B7280',
          }} data-testid="push-toggle-state">{pushOn ? 'ON' : 'OFF'}</span>
        </div>

        {settingsItems.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.id}
              className={`settings-item ${item.danger ? 'danger' : ''}`}
              onClick={() => handleItemClick(item.action)}
              data-testid={`settings-item-${item.action}`}
            >
              <div className="settings-item-icon"><Icon size={24} /></div>
              <span className="settings-item-label">{item.label}</span>
            </div>
          );
        })}
      </div>

      {/* Timezone picker */}
      {openDialog === 'timezone' && (
        <StaticDialog title="Choose your timezone" onClose={() => { setOpenDialog(null); setTzSearch(''); }} testid="tz-picker-dialog">
          <input
            type="search"
            value={tzSearch}
            onChange={(e) => setTzSearch(e.target.value)}
            placeholder="Search by city or region (e.g. Sydney, London, Tokyo)"
            data-testid="tz-search-input"
            autoFocus
            style={{ width: '100%', padding: '12px 14px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 15, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 12 }}
          />
          {detectedTz && detectedTz !== currentTz && (
            <button
              onClick={() => pickTimezone(detectedTz)}
              disabled={savingTz}
              data-testid="tz-use-detected"
              style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px dashed #2C4E46', background: '#F0FDF4', color: '#166534', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 10, textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <span>Use detected · {tzLabel(detectedTz)}</span>
              <span style={{ fontSize: 11, color: '#166534', opacity: 0.8 }}>{detectedTz}</span>
            </button>
          )}
          <div style={{ maxHeight: '50vh', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 }} data-testid="tz-list">
            {filteredTimezones.length === 0 ? (
              <p style={{ padding: 20, textAlign: 'center', color: '#6B7280', fontSize: 14 }}>No zones match "{tzSearch}"</p>
            ) : (
              filteredTimezones.slice(0, 200).map(tz => {
                const isCurrent = tz === currentTz;
                const isDetected = tz === detectedTz;
                return (
                  <button
                    key={tz}
                    onClick={() => pickTimezone(tz)}
                    disabled={savingTz || isCurrent}
                    data-testid={`tz-option-${tz.replace(/\//g, '-')}`}
                    style={{
                      width: '100%', textAlign: 'left', padding: '12px 14px',
                      background: isCurrent ? '#F0FDF4' : '#fff',
                      border: 'none', borderBottom: '1px solid #f3f4f6',
                      cursor: isCurrent ? 'default' : (savingTz ? 'wait' : 'pointer'),
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#111' }}>
                        {tzLabel(tz)}
                        {isCurrent && <span style={{ marginLeft: 8, fontSize: 11, color: '#166534', fontWeight: 700 }}>· CURRENT</span>}
                        {isDetected && !isCurrent && <span style={{ marginLeft: 8, fontSize: 11, color: '#2C4E46', fontWeight: 700 }}>· DETECTED</span>}
                      </p>
                      <p style={{ margin: 0, fontSize: 12, color: '#6B7280' }}>{tz}</p>
                    </div>
                    <span style={{ fontSize: 12, color: '#9CA3AF', whiteSpace: 'nowrap' }}>{tzOffset(tz)}</span>
                  </button>
                );
              })
            )}
          </div>
          {filteredTimezones.length > 200 && (
            <p style={{ marginTop: 8, fontSize: 12, color: '#6B7280', textAlign: 'center' }}>
              Showing first 200 of {filteredTimezones.length} — refine your search for more.
            </p>
          )}
        </StaticDialog>
      )}

      {/* About dialog */}
      {openDialog === 'about' && (
        <StaticDialog title="About 6th Mate" onClose={() => setOpenDialog(null)} testid="about-dialog">
          <p><strong>6th Mate</strong> connects retirees and later-life mentors with people seeking real-world advice, via paid video calls.</p>
          <p style={{ marginTop: 12 }}>Version <strong>v{APP_VERSION}</strong></p>
          <p style={{ marginTop: 12, color: '#6B7280', fontSize: 13 }}>Built in Australia. Support: <a href="mailto:support@6thmate.com" style={{ color: '#2C4E46', fontWeight: 600 }}>support@6thmate.com</a></p>
        </StaticDialog>
      )}

      {/* Terms dialog */}
      {openDialog === 'terms' && (
        <StaticDialog title="Terms of Use" onClose={() => setOpenDialog(null)} testid="terms-dialog">
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.6, margin: 0 }}>{TERMS_TEXT}</pre>
        </StaticDialog>
      )}

      {/* Privacy dialog */}
      {openDialog === 'privacy' && (
        <StaticDialog title="Privacy Policy" onClose={() => setOpenDialog(null)} testid="privacy-dialog">
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.6, margin: 0 }}>{PRIVACY_TEXT}</pre>
        </StaticDialog>
      )}

      {/* Report dialog */}
      {openDialog === 'report' && (
        <StaticDialog title="Report a problem" onClose={() => setOpenDialog(null)} testid="report-dialog">
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Category</label>
          <select
            value={reportCategory}
            onChange={(e) => setReportCategory(e.target.value)}
            data-testid="report-category-select"
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, background: '#fff', marginBottom: 12 }}
          >
            {REPORT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Describe the problem</label>
          <textarea
            value={reportMessage}
            onChange={(e) => setReportMessage(e.target.value)}
            data-testid="report-message-input"
            rows={6}
            placeholder="Tell us what went wrong. Include steps to reproduce if you can."
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, resize: 'vertical', fontFamily: 'inherit' }}
          />
          <p style={{ fontSize: 12, color: '#6B7280', marginTop: 8 }}>We'll email your report to support@6thmate.com and reply to {user?.email || 'your account email'}.</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              onClick={() => setOpenDialog(null)}
              style={{ flex: 1, padding: '12px', borderRadius: 999, border: '1px solid #d1d5db', background: '#fff', fontWeight: 600, cursor: 'pointer' }}
            >Cancel</button>
            <button
              onClick={submitReport}
              disabled={reportSubmitting}
              data-testid="report-submit-btn"
              style={{ flex: 1, padding: '12px', borderRadius: 999, border: 'none', background: '#2C4E46', color: '#fff', fontWeight: 700, cursor: reportSubmitting ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              {reportSubmitting ? 'Sending…' : (<><Send size={16} /> Send report</>)}
            </button>
          </div>
        </StaticDialog>
      )}
    </div>
  );
};

const StaticDialog = ({ title, onClose, children, testid }) => (
  <div
    onClick={onClose}
    data-testid={testid}
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 520, width: '100%', maxHeight: '85vh', overflowY: 'auto', position: 'relative' }}
    >
      <button
        onClick={onClose}
        aria-label="Close"
        style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}
      ><X size={22} /></button>
      <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 22, fontWeight: 700, color: '#1F2937', marginBottom: 16 }}>{title}</h2>
      <div style={{ color: '#374151', fontSize: 15, lineHeight: 1.5 }}>{children}</div>
    </div>
  </div>
);

export default Settings;
